/**
 * NK Product section
 * - Variant picker: applies an optimistic instant update from already-loaded
 *   product JSON (price, variant id, sticky bar, featured image) so the UI does
 *   NOT wait for the network. The server-rendered HTML is still fetched in the
 *   background and dispatches VariantUpdateEvent so media-gallery, product-form,
 *   and SEO stay in sync.
 */

import { VariantUpdateEvent } from '@theme/events';

/**
 * <nk-variant-picker>
 * Tracks selected option values, applies an instant local update, then fetches
 * server-rendered product HTML and dispatches VariantUpdateEvent for theme
 * gallery / form listeners.
 */
class NkVariantPicker extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl || '';
    this.formId = this.dataset.formId;
    this.section = document.getElementById('shopify-section-' + this.sectionId) || this.closest('.shopify-section');

    this.inputs = Array.from(this.querySelectorAll('input[type="radio"][data-option-index]'));
    this.selects = Array.from(this.querySelectorAll('select[data-option-index]'));

    this.inputs.forEach((input) => {
      input.addEventListener('change', this.handleOptionChange.bind(this));
    });

    this.selects.forEach((select) => {
      select.addEventListener('change', this.handleOptionChange.bind(this));
    });

    this.fetchProduct();
    this.updateSelectedPill(this.getSelectedOptions());
  }

  /**
   * Loads product JSON used for the optimistic instant update and the sticky bar.
   */
  fetchProduct() {
    if (!this.productUrl) return;
    const url = this.productUrl + '.js';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((resp) => (resp.ok ? resp.json() : null))
      .then((data) => {
        if (data) this.productData = data;
      })
      .catch(() => {
        /* non-fatal */
      });
  }

  /**
   * Handles option radio change.
   * 1. Applies an instant optimistic update from local product JSON (no network).
   * 2. Fetches server HTML in the background and dispatches variant:update.
   * @param {Event} event
   */
  handleOptionChange(event) {
    const selected = this.getSelectedOptions();
    this.updateSelectedPill(selected);

    // Optimistic instant update: no network wait. Uses product JSON loaded on connect.
    const localVariant = this.findVariantFromSelected(selected);
    if (localVariant) {
      this.updateVariantId(localVariant);
      this.updatePrice(localVariant);
      this.updateAtcButton(localVariant);
      this.updateStickyBar(localVariant);
      this.selectGalleryMedia(localVariant);
    }

    const optionValueIds = this.getSelectedOptionValueIds();
    if (!optionValueIds.length) return;

    const triggerInput = event.target instanceof HTMLInputElement ? event.target : null;
    const triggerSelect = event.target instanceof HTMLSelectElement ? event.target : null;
    const sourceId =
      triggerInput?.dataset?.optionValueId ??
      triggerSelect?.selectedOptions?.[0]?.dataset?.optionValueId ??
      '';

    this.fetchUpdatedProduct(optionValueIds, sourceId);
  }

  /**
   * Finds the matching variant in the locally loaded product JSON.
   * @param {string[]} selected - Selected option values keyed by option index.
   * @returns {object | null}
   */
  findVariantFromSelected(selected) {
    const variants = this.productData?.variants;
    if (!Array.isArray(variants) || !variants.length) return null;

    return (
      variants.find((variant) => {
        const opts = variant.options || [variant.option1, variant.option2, variant.option3];
        for (let i = 0; i < selected.length; i++) {
          if (selected[i] == null) continue;
          if (opts[i] !== selected[i]) return false;
        }
        return true;
      }) || null
    );
  }

  /**
   * Switches the main gallery to the variant's featured media instantly,
   * by selecting the matching slide in place (no DOM rebuild).
   * @param {object} variant
   */
  selectGalleryMedia(variant) {
    if (!this.section) return;

    const gallery = this.section.querySelector('media-gallery');
    const slideshow = gallery?.slideshow || this.section.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const slides = slideshow.slides || Array.from(slideshow.querySelectorAll('slideshow-slide'));
    if (!slides.length) return;

    let index = -1;

    const mediaId = variant.featured_media?.id;
    if (mediaId != null) {
      index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
    }

    // Fallback: match by image filename if no media id mapping is found.
    if (index < 0) {
      const src = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
      const file = src ? src.split('?')[0].split('/').pop() : '';
      if (file) {
        index = slides.findIndex((slide) => {
          const img = slide.querySelector('img');
          const isrc = img ? img.currentSrc || img.src : '';
          return isrc.includes(file);
        });
      }
    }

    if (index >= 0) {
      slideshow.select(index, undefined, { animate: false });
    }
  }

  /**
   * Returns selected option values keyed by option index.
   */
  getSelectedOptions() {
    const values = [];

    this.inputs.forEach((input) => {
      if (input.checked) {
        const idx = Number(input.dataset.optionIndex);
        values[idx] = input.dataset.optionValue;
      }
    });

    this.selects.forEach((select) => {
      const idx = Number(select.dataset.optionIndex);
      const selectedOption = select.selectedOptions[0];
      if (selectedOption) {
        values[idx] = selectedOption.dataset.optionValue || selectedOption.value;
      }
    });

    return values;
  }

  /**
   * Returns Shopify option value IDs for the active inputs and selects.
   * @returns {string[]}
   */
  getSelectedOptionValueIds() {
    const indices = Array.from(this.querySelectorAll('[data-option-index]')).map((el) =>
      Number(el.dataset.optionIndex)
    );
    const optionCount = indices.length ? Math.max(...indices) + 1 : 0;
    const ids = [];

    for (let i = 0; i < optionCount; i++) {
      const radio = this.querySelector(`input[type="radio"][data-option-index="${i}"]:checked`);
      if (radio?.dataset.optionValueId) {
        ids.push(radio.dataset.optionValueId);
        continue;
      }

      const select = this.querySelector(`select[data-option-index="${i}"]`);
      const selectedOption = select?.selectedOptions?.[0];
      if (selectedOption?.dataset?.optionValueId) {
        ids.push(selectedOption.dataset.optionValueId);
      }
    }

    return ids.filter(Boolean);
  }

  /**
   * Builds the product page request URL with option_values (matches theme variant-picker).
   * @param {string[]} optionValueIds
   * @returns {string}
   */
  buildRequestUrl(optionValueIds) {
    return `${this.productUrl}?option_values=${optionValueIds.join(',')}`;
  }

  /**
   * Fetches server-rendered product HTML in the background and dispatches
   * VariantUpdateEvent. The visible buy box has already been updated optimistically,
   * so this fetch reconciles SEO, the product form, and any B2B fields.
   * @param {string[]} optionValueIds
   * @param {string} sourceId
   */
  fetchUpdatedProduct(optionValueIds, sourceId) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const requestUrl = this.buildRequestUrl(optionValueIds);

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const textContent = html.querySelector('nk-variant-picker script[type="application/json"]')?.textContent;
        if (!textContent) return;

        const variant = JSON.parse(textContent);
        const eventSourceId = sourceId || String(variant.id ?? '');

        // Reconcile against the authoritative server variant (usually a no-op
        // because the optimistic update already applied the same values).
        this.updateVariantId(variant);
        this.updatePrice(variant);
        this.updateAtcButton(variant);
        this.updateStickyBar(variant);
        updateProductSeo(html, variant, this.sectionId, this.productUrl);

        this.section?.dispatchEvent(
          new VariantUpdateEvent(variant, eventSourceId, {
            html,
            productId: this.dataset.productId ?? '',
          })
        );

        this.dispatchEvent(
          new CustomEvent('nk:variant-change', {
            bubbles: true,
            detail: { variant, productData: this.productData },
          })
        );
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      });
  }

  /**
   * Updates the hidden variant id input on the product form.
   * @param {object} variant
   */
  updateVariantId(variant) {
    if (!this.formId) return;
    const form = document.getElementById(this.formId);
    if (!form) return;
    const idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;
  }

  /**
   * Updates price and compare-at price in the buy box.
   * @param {object} variant
   */
  updatePrice(variant) {
    if (!this.section) return;
    const priceEl = this.section.querySelector('.nk-product__price');
    const compareEl = this.section.querySelector('.nk-product__compare-price');
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  /**
   * Updates main ATC button availability state.
   * @param {object} variant
   */
  updateAtcButton(variant) {
    if (!this.section) return;
    const btn = this.section.querySelector('.nk-product__cta');
    if (!btn) return;
    const labelEl = btn.querySelector('.add-to-cart-text__content span span');
    if (variant.available) {
      btn.disabled = false;
      if (labelEl) labelEl.textContent = window.theme?.translations?.addToCart || labelEl.textContent;
    } else {
      btn.disabled = true;
      if (labelEl) labelEl.textContent = window.theme?.translations?.soldOut || 'Sold out';
    }
  }

  /**
   * Syncs the sticky add-to-cart bar with the active variant.
   * @param {object} variant
   */
  updateStickyBar(variant) {
    if (!this.section) return;
    const sticky = this.section.querySelector('sticky-add-to-cart');
    if (!sticky) return;

    const priceEl = sticky.querySelector('[data-nk-sticky-price]');
    const variantEl = sticky.querySelector('[data-nk-sticky-variant]');
    const stickyBtn = sticky.querySelector('[ref="addToCartButton"]');
    const labelEl = sticky.querySelector('[data-nk-sticky-button-label]');
    const actionLabelEl = sticky.querySelector('[data-nk-sticky-action-label]');
    const priceSepEl = sticky.querySelector('[data-nk-sticky-price-sep]');
    const stickyImage = sticky.querySelector('[data-testid="sticky-product-image"]');

    if (priceEl) priceEl.textContent = formatMoney(variant.price);

    if (variantEl) {
      const swatchIdx = this.querySelector('[data-nk-selected-pill]')?.dataset.swatchOptionIndex;
      const metaLabel =
        swatchIdx !== undefined && swatchIdx !== '' && variant.options
          ? variant.options[Number(swatchIdx)]
          : variant.title;

      if (metaLabel) {
        variantEl.textContent = metaLabel;
        variantEl.hidden = false;
      } else {
        variantEl.hidden = true;
      }
    }

    const featuredSrc = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
    if (featuredSrc && stickyImage) {
      stickyImage.src = featuredSrc;
    }

    sticky.querySelectorAll('[data-sticky-option]').forEach((button) => {
      const optionIndex = button.dataset.optionIndex;
      const optionValue = button.dataset.optionValue;
      const isActive = variant.options && variant.options[Number(optionIndex)] === optionValue;
      button.classList.toggle('is-active', Boolean(isActive));
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    sticky.dataset.variantAvailable = variant.available ? 'true' : 'false';
    sticky.dataset.currentVariantId = String(variant.id);

    if (stickyBtn) {
      stickyBtn.disabled = !variant.available;

      if (!variant.available && labelEl) {
        labelEl.textContent = window.theme?.translations?.soldOut || 'Sold out';
        return;
      }

      if (actionLabelEl) {
        actionLabelEl.textContent = window.theme?.translations?.addToCart || 'Add to Cart';
      }

      if (priceSepEl) priceSepEl.hidden = false;
      if (priceEl) priceEl.hidden = false;
    }
  }

  /**
   * Updates the "Selected: …" bar from the active option values.
   */
  updateSelectedPill(selected) {
    const pill = this.querySelector('[data-nk-selected-pill]');
    if (!pill) return;
    const valueEl = pill.querySelector('[data-nk-selected-value]');
    if (!valueEl) return;

    const swatchIdx = pill.dataset.swatchOptionIndex;
    if (swatchIdx !== undefined && swatchIdx !== '' && selected[Number(swatchIdx)]) {
      valueEl.textContent = selected[Number(swatchIdx)];
      return;
    }

    const label = selected.filter(Boolean).join(', ');
    if (label) valueEl.textContent = label;
  }
}

if (!customElements.get('nk-variant-picker')) {
  customElements.define('nk-variant-picker', NkVariantPicker);
}

/** @type {readonly string[]} */
const SEO_OG_PROPERTIES = [
  'og:url',
  'og:image',
  'og:image:secure_url',
  'og:image:width',
  'og:image:height',
  'og:price:amount',
  'og:price:currency',
];

/**
 * Builds the shareable variant URL used for canonical and og:url.
 * @param {string} productUrl
 * @param {string|number} variantId
 * @returns {string}
 */
function buildVariantUrl(productUrl, variantId) {
  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('variant', String(variantId));
  return url.toString();
}

/**
 * Returns a normalized HTTPS image URL for a variant featured media field.
 * @param {object} variant
 * @returns {string | undefined}
 */
function getVariantImageUrl(variant) {
  const src = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
  if (!src) return undefined;
  if (src.startsWith('http')) return src;
  if (src.startsWith('//')) return `https:${src}`;
  return src;
}

/**
 * Finds a Product node inside JSON-LD that may be nested or use @graph.
 * @param {object} data
 * @returns {object | undefined}
 */
function findProductJsonLdNode(data) {
  if (!data || typeof data !== 'object') return undefined;
  if (data['@type'] === 'Product') return data;

  if (Array.isArray(data['@graph'])) {
    return data['@graph'].find((node) => node?.['@type'] === 'Product');
  }

  return undefined;
}

/**
 * Updates offer fields on Product JSON-LD for the active variant.
 * @param {HTMLScriptElement} scriptEl
 * @param {object} variant
 * @param {string} variantUrl
 */
function patchProductJsonLd(scriptEl, variant, variantUrl) {
  if (!scriptEl?.textContent || !variant?.id) return;

  try {
    const data = JSON.parse(scriptEl.textContent);
    const product = findProductJsonLdNode(data);
    if (!product) return;

    const price = (variant.price / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'USD';
    const availability = variant.available
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';

    const applyOffer = (offer) => {
      if (!offer || typeof offer !== 'object') return;
      offer.price = price;
      offer.priceCurrency = currency;
      offer.availability = availability;
      offer.url = variantUrl;
      if (variant.sku) offer.sku = variant.sku;
    };

    if (Array.isArray(product.offers)) {
      product.offers.forEach(applyOffer);
    } else {
      applyOffer(product.offers);
    }

    if (variant.sku) product.sku = variant.sku;

    const imageUrl = getVariantImageUrl(variant);
    if (imageUrl) product.image = imageUrl;

    scriptEl.textContent = JSON.stringify(data);
  } catch (_) {
    /* non-fatal */
  }
}

/**
 * Copies a meta[property] tag from fetched head into the live document.
 * @param {string} property
 * @param {Document} fetchedHtml
 */
function syncMetaProperty(property, fetchedHtml) {
  const source = fetchedHtml.querySelector(`head meta[property="${property}"]`);
  if (!source?.content) return;

  let target = document.querySelector(`meta[property="${property}"]`);
  if (target) {
    target.content = source.content;
    return;
  }

  const clone = source.cloneNode(true);
  document.head.appendChild(clone);
}

/**
 * Sets canonical and og:url to the active variant URL.
 * @param {string} variantUrl
 */
function syncVariantUrls(variantUrl) {
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.href = variantUrl;

  let ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) {
    ogUrl.content = variantUrl;
    return;
  }

  ogUrl = document.createElement('meta');
  ogUrl.setAttribute('property', 'og:url');
  ogUrl.content = variantUrl;
  document.head.appendChild(ogUrl);
}

/**
 * Updates Open Graph price and image tags from the active variant.
 * @param {object} variant
 */
function patchOpenGraphFromVariant(variant) {
  const priceMeta = document.querySelector('meta[property="og:price:amount"]');
  if (priceMeta && variant.price != null) {
    priceMeta.content = (variant.price / 100).toFixed(2);
  }

  const currencyMeta = document.querySelector('meta[property="og:price:currency"]');
  if (currencyMeta && window.Shopify?.currency?.active) {
    currencyMeta.content = window.Shopify.currency.active;
  }

  const imageUrl = getVariantImageUrl(variant);
  if (!imageUrl) return;

  ['og:image', 'og:image:secure_url'].forEach((property) => {
    const meta = document.querySelector(`meta[property="${property}"]`);
    if (meta) meta.content = imageUrl;
  });
}

/**
 * Syncs canonical, Open Graph, and JSON-LD SEO data after a variant change.
 * @param {Document} fetchedHtml
 * @param {object} variant
 * @param {string} sectionId
 * @param {string} productUrl
 */
function updateProductSeo(fetchedHtml, variant, sectionId, productUrl) {
  if (!variant?.id || !productUrl) return;

  const variantUrl = buildVariantUrl(productUrl, variant.id);
  const sectionSelector = `#shopify-section-${sectionId}`;

  if (fetchedHtml.querySelector('head')) {
    SEO_OG_PROPERTIES.forEach((property) => syncMetaProperty(property, fetchedHtml));

    const fetchedCanonical = fetchedHtml.querySelector('head link[rel="canonical"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (fetchedCanonical?.href && canonical) {
      canonical.href = fetchedCanonical.href;
    }
  }

  syncVariantUrls(variantUrl);

  const fetchedSchema = fetchedHtml.querySelector(
    `${sectionSelector} script[data-nk-product-schema], ${sectionSelector} script[type="application/ld+json"]`
  );
  const currentSchema = document.querySelector(
    `${sectionSelector} script[data-nk-product-schema], ${sectionSelector} script[type="application/ld+json"]`
  );

  if (fetchedSchema && currentSchema) {
    currentSchema.textContent = fetchedSchema.textContent;
  }

  if (currentSchema) {
    patchProductJsonLd(currentSchema, variant, variantUrl);
  }

  patchOpenGraphFromVariant(variant);

  try {
    const browserUrl = new URL(window.location.href);
    browserUrl.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', browserUrl.toString());
  } catch (_) {
    /* ignore */
  }
}

/**
 * Lightweight money formatter mirroring Shopify's default {{ price | money }}.
 * @param {number} cents
 */
function formatMoney(cents) {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    const fmt = window.theme?.moneyFormat || '${{amount}}';
    return window.Shopify.formatMoney(cents, fmt);
  }
  const dollars = (cents / 100).toFixed(2);
  return '$' + dollars;
}